"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LightshowV1 = exports.EMBEDDED_PADDING_RATIO = void 0;
const toolbox_1 = require("@streem/toolbox");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const logger_1 = require("../util/logger");
const lightshow_utils_1 = require("./lightshow_utils");
const FRAME_NUMBER_BITS = 16;
const MAX_TRANSFORMS_TO_KEEP = 256;
const BITS = Object.freeze(Array.from({ length: FRAME_NUMBER_BITS }, (_, i) => i));
// Expressing our padding as a ratio requires fewer assumptions. It's established
// in the iOS ImageUtils.swift code that we expect 8px padding at 1280px width
// giving us 8/1280 or 0.00625.
exports.EMBEDDED_PADDING_RATIO = 0.00625;
class LightshowV1 {
    constructor() {
        this.cameraFrames = new Array(MAX_TRANSFORMS_TO_KEEP);
        this.unsubscribe = new rxjs_1.Subject();
        this.canvas = document.createElement('canvas');
        logger_1.logger.info('Using LightshowV1');
    }
    connect(dataTrack) {
        dataTrack
            .pipe((0, operators_1.filter)(msg => msg.type === 'cameraFrame' && 'cameraFrame' in msg), (0, operators_1.map)(msg => msg.cameraFrame), (0, operators_1.takeUntil)(this.unsubscribe))
            .subscribe(frame => {
            if (!frame.frameNumber) {
                logger_1.logger.warn('Received cameraFrame without frameNumber', frame);
                return;
            }
            // Set the current camera frame last so we don't mistakenly drop a frame.
            this.cameraFrames[frame.frameNumber % MAX_TRANSFORMS_TO_KEEP] = frame;
        });
    }
    disconnect() {
        logger_1.logger.debug('Clearing subscriptions');
        this.unsubscribe.next(undefined);
        this.canvas.remove();
        this.cameraFrames.splice(0, MAX_TRANSFORMS_TO_KEEP);
    }
    getLongEdgeOffset(longEdge) {
        return Math.floor(longEdge * exports.EMBEDDED_PADDING_RATIO);
    }
    getLightshowAvoidingRect(videoElement) {
        const width = videoElement.videoWidth;
        const height = videoElement.videoHeight;
        const isPortrait = width < height;
        return {
            x: isPortrait ? 0 : this.getLongEdgeOffset(width),
            y: isPortrait ? this.getLongEdgeOffset(height) : 0,
            width: isPortrait ? width : width - this.getLongEdgeOffset(width),
            height: isPortrait ? height - this.getLongEdgeOffset(height) : height,
        };
    }
    extractCameraFrame(videoElement) {
        const dimensions = {
            width: videoElement.videoWidth,
            height: videoElement.videoHeight,
        };
        const isPortrait = dimensions.width < dimensions.height;
        const shortEdge = isPortrait ? dimensions.width : dimensions.height;
        const sectionWidth = shortEdge / FRAME_NUMBER_BITS;
        const halfSectionWidth = sectionWidth / 2;
        const canvasWidth = isPortrait ? shortEdge : 1;
        const canvasHeight = isPortrait ? 1 : shortEdge;
        if (this.canvas.width !== canvasWidth || this.canvas.height !== canvasHeight) {
            this.canvas.width = canvasWidth;
            this.canvas.height = canvasHeight;
        }
        const ctx = this.canvas.getContext('2d');
        (0, toolbox_1.invariant)(ctx, 'Unable to capture 2D rendering context');
        // Draw a 1px wide slice of the video frame where our lightshow should be onto the canvas.
        ctx.drawImage(videoElement, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvasWidth, canvasHeight);
        // Parse the bits across the x-axis from right to left in portrait, or down the y-axis in landscape
        let frameNumber = 0;
        BITS.forEach(i => {
            frameNumber <<= 1;
            const step = Math.round(sectionWidth * i + halfSectionWidth);
            const dark = isPortrait
                ? (0, lightshow_utils_1.isDark)(shortEdge - step, 0, imageData)
                : (0, lightshow_utils_1.isDark)(0, step, imageData);
            frameNumber += dark ? 0 : 1;
        });
        const possibleFrame = this.cameraFrames[frameNumber % MAX_TRANSFORMS_TO_KEEP];
        return (possibleFrame === null || possibleFrame === void 0 ? void 0 : possibleFrame.frameNumber) === frameNumber ? possibleFrame : undefined;
    }
}
exports.LightshowV1 = LightshowV1;
//# sourceMappingURL=lightshow_v1.js.map